import { CalendarDaysIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import Currency from '../../../components/currency/Currency';
import { PREFERRED_LOCALE } from '../../../constants';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useEntriesV2 } from '../../../queries/use-entries';
import { useUpdateWorksheetEntries } from '../../../queries/use-update-worksheet-entries';
import { NatureEnum } from '../../../types/nature';
import { Worksheet, WorksheetEntryV2 } from '../../../types/worksheet';

import AttachmentMenu from '../../worksheet-v2/entries/attachments/AttachmentMenu';
import EntriesSubtotal from '../../worksheet-v2/entries/EntriesSubtotal';
import PreviewSection from '../PreviewSection';

type EntriesPreviewProps = {
    worksheet: Worksheet;
};
function EntriesPreview({ worksheet }: EntriesPreviewProps): JSX.Element {
    const entriesQuery = useEntriesV2(worksheet.id, { sort: '' });
    const [openFileDrawer, setOpenFileDrawer] = useState<string>();

    if (!('entries' in worksheet)) {
        return <></>;
    }

    if (entriesQuery.isLoading || !entriesQuery.isSuccess) {
        return <></>;
    }

    const entries = entriesQuery.data.data;

    return (
        <PreviewSection title={`Entries (${worksheet.entries.length})`}>
            {entries.length === 0 ? (
                <></>
            ) : (
                <div className="mt-2">
                    <div className="grid grid-cols-3">
                        <p className="uppercase text-gray-600 text-xs col-span-2">Description</p>
                        <p className="uppercase text-gray-600 text-xs text-right">Amount</p>
                        <div className="grid grid-cols-3 col-span-full gap-y-2 border-t mt-2 pt-2">
                            {entries.map((entry) => (
                                <EntryPreview
                                    key={entry.id}
                                    entry={entry}
                                    worksheet={worksheet}
                                    showFileDrawer={openFileDrawer === entry.id}
                                    onFileDrawerClick={(id): void => {
                                        setOpenFileDrawer(id === openFileDrawer ? undefined : id);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <EntriesSubtotal entries={entries} />
                </div>
            )}
        </PreviewSection>
    );
}

type EntryPreviewProps = {
    entry: WorksheetEntryV2;
    worksheet: Worksheet;
    showFileDrawer: boolean;
    onFileDrawerClick: (id: string) => void;
};

function EntryPreview({ entry, worksheet, onFileDrawerClick, showFileDrawer }: EntryPreviewProps): JSX.Element {
    const updateEntriesQuery = useUpdateWorksheetEntries(worksheet.id);
    const { readOnly } = useWorkpaperContext();

    function update(attachments: string[]): void {
        updateEntriesQuery.mutate([{ id: entry.id, fields: { attachments } }], {});
    }

    return (
        <>
            <p className="col-span-full text-sm text-gray-700">{entry.description || '-'}</p>
            <p className="text-sm text-gray-700 flex items-center">
                <CalendarDaysIcon className="text-gray-500 h-4 w-4 mr-2" />
                {new Date(entry.date).toLocaleDateString(PREFERRED_LOCALE)}
            </p>
            <div className="text-sm text-gray-700 w-fit mx-auto">
                <AttachmentMenu
                    attachments={entry.attachments}
                    worksheetFiles={worksheet.files}
                    defaultFolderId={worksheet.id}
                    onAttach={update}
                    disabled={readOnly}
                    isOpen={showFileDrawer}
                    onOpenToggle={(): void => {
                        onFileDrawerClick(entry.id);
                    }}
                />
            </div>
            <p className="text-sm text-gray-700 text-right">
                <Currency value={entry.amount} withParentheses={Boolean(entry.nature === NatureEnum.CREDIT)} />
            </p>
            <hr className="col-span-full" />
        </>
    );
}

export default EntriesPreview;
